import axios from 'axios';
import * as React from 'react';
import Dropdown from '../../../components_v2/dropdown/Dropdown';
import { DropdownData } from '../../../components_v2/dropdown/model/Model';
import { URL_FOUNDATION } from '../../../config/keys';
import { DefaultButton } from '../../../styles/global/css/GlobalButton';
import { GreySidely3 } from '../../../styles/global/css/Utils';
import { Translate, translateToString } from '../../../styles/global/translate';
import { importWrapper } from '../../../utils/import';
import { AlertCall, AlertRes } from '../../alert/AlertProvider';
import { creationPopupDropdownStyle } from '../../../components_v2/popup/style/CreationFormStyle';
import { launchToastError } from '../../forms/visual-form-editor/visualFormEditor';
import { getBrands } from '../../../components_v2/toolbarFilter/brands/action';
import { getCategories } from '../../products/action';
import { FlexDiv } from '../../products/style';
import { bestMatch, normalizeHeader } from '../../promotions/import';

// The popup imports of the product settings (brands, categories), in the
// spirit of the product import: a mapping popup auto-filled from the file's
// headers, one transaction on the server, duplicates ignored and reported.

export type Target = { key: string, label: () => string, candidates: string[], required?: boolean };
type ImportRow = Record<string, string | undefined>;
type Report = { created: number, skipped: number[] };

// The column-mapping popup shared by the settings imports: auto-filled from
// the file's headers, returns undefined when the user closes it
export async function mapColumnsPopup(alert: AlertCall, rawHeaders: unknown[], targets: Target[], hintKey: string): Promise<Map<string, number> | undefined> {
	const alertRes = await alert<Map<string, number>>({
		title: <Translate id='general.import' />,
		buttonMargin: '0px',
		noButtons: true,
		overflow: 'unset',
		width: 'min(560px, 95vw)',
		content: (_, onOk) => {
			const normHeaders = React.useMemo(() => rawHeaders.map(normalizeHeader), []);
			const columnOptions = React.useMemo<DropdownData<number>[]>(
				() => rawHeaders.map((h, i) => ({ label: String(h ?? '').trim() || `${translateToString('import.promotions.file_column')} ${i + 1}`, value: i })),
				[],
			);
			const init = React.useMemo(() => {
				const used = new Set<number>();
				const mapping = new Map<string, number>();
				for (const target of targets) {
					const i = bestMatch(normHeaders, [target.label(), ...target.candidates], used);
					if (i !== undefined) {
						used.add(i);
						mapping.set(target.key, i);
					}
				}
				return mapping;
			}, []);
			const [mapping, setMapping] = React.useState(init);
			const submit = () => {
				if (targets.some(t => t.required && !mapping.has(t.key))) {
					launchToastError(translateToString('import.products.name_required'));
					return;
				}
				onOk(mapping);
			};
			return <FlexDiv flow='column' align='stretch' gap='12px' style={{ width: '100%', textAlign: 'initial' }}>
				<p style={{ margin: 0, fontSize: '12px', color: GreySidely3 }}><Translate id={hintKey} /></p>
				<div style={{ display: 'grid', gridTemplateColumns: '160px minmax(0, 1fr)', alignItems: 'center', rowGap: '10px', columnGap: '12px', width: '100%' }}>
					{targets.map(target => <React.Fragment key={target.key}>
						<h5 style={{ margin: 0, fontSize: '13px', fontWeight: 500 }}>{target.label()}{target.required ? ' *' : ''}</h5>
						<Dropdown<number>
							cancellable
							dropdownStyle={creationPopupDropdownStyle}
							name=''
							datalist={columnOptions}
							selectedValue={mapping.has(target.key) ? columnOptions.find(o => o.value === mapping.get(target.key)) : undefined}
							onChange={v => setMapping(prev => {
								const next = new Map(prev);
								if (v.value === undefined || v.value === null) next.delete(target.key);
								else next.set(target.key, v.value);
								return next;
							})}
						/>
					</React.Fragment>)}
				</div>
				<FlexDiv justify='center' style={{ width: '100%' }}>
					<DefaultButton margin='15px 0 0 0' onClick={submit}><Translate id='general.import' /></DefaultButton>
				</FlexDiv>
			</FlexDiv>;
		},
	});
	return alertRes.res === AlertRes.Break ? undefined : alertRes.data;
}

// Rows of the mapped columns, with their 1-based file lines — empty rows drop
export function mappedRows(data: unknown[], mapping: Map<string, number>, targets: Target[]): { rows: ImportRow[], lines: number[] } {
	const rows: ImportRow[] = [];
	const lines: number[] = [];
	data.forEach((rowValues, rowIndex) => {
		if (rowIndex === 0 || !Array.isArray(rowValues)) return;
		if (rowValues.length === 0 || rowValues.every(value => value === undefined || value === null || value === '')) return;
		const row: ImportRow = {};
		for (const target of targets) {
			const index = mapping.get(target.key);
			if (index === undefined) continue;
			const value = rowValues[index];
			const text = value === undefined || value === null ? '' : String(value).trim();
			if (text) row[target.key] = text;
		}
		rows.push(row);
		lines.push(rowIndex + 1);
	});
	return { rows, lines };
}

function importEntities(alert: AlertCall, targets: Target[], hintKey: string, post: (rows: ImportRow[]) => Promise<Report>, onDone: () => void) {
	importWrapper<never>(async(data) => {
		if (!Array.isArray(data[0])) {
			launchToastError(translateToString('import.promotions.errors.bad_file'));
			return;
		}
		const rawHeaders: unknown[] = data[0];
		const mapping = await mapColumnsPopup(alert, rawHeaders, targets, hintKey);
		if (!mapping) return;

		// rows keep their file line so the summary can name the skipped ones
		const { rows, lines } = mappedRows(data, mapping, targets);
		if (rows.length === 0) {
			launchToastError(translateToString('import.promotions.errors.bad_file'));
			return;
		}
		post(rows).then(report => {
			onDone();
			alert({
				title: translateToString('import.products.summary_title'),
				width: 'min(480px, 95vw)',
				content: () => <FlexDiv flow='column' align='stretch' gap='10px'>
					<p style={{ margin: 0, fontSize: '13px' }}>
						{translateToString('import.settings.summary', [
							['created', String(report.created)],
							['skipped', String(report.skipped.length)],
						])}
					</p>
					{report.skipped.length > 0 && <p style={{ margin: 0, fontSize: '12px', color: GreySidely3 }}>
						{translateToString('import.settings.skipped_lines', [['lines', report.skipped.slice(0, 30).map(i => lines[i - 1] ?? i).join(', ')]])}
					</p>}
				</FlexDiv>,
			});
		}).catch(e => {
			console.error(e);
			launchToastError(translateToString('import.products.failed'));
		});
	}, alert);
}

export function importBrands(alert: AlertCall, onDone: () => void) {
	importEntities(
		alert,
		[
			{ key: 'name', label: () => translateToString('name'), candidates: ['name', 'nom', 'marque', 'brand'], required: true },
			{ key: 'description', label: () => translateToString('description'), candidates: ['description'] },
		],
		'import.settings.brands_hint',
		rows => axios.post(`${URL_FOUNDATION}/api/brands/import`, { brands: rows }).then(res => res.data),
		onDone,
	);
}

export function importCategories(alert: AlertCall, onDone: () => void) {
	importEntities(
		alert,
		[
			{ key: 'name', label: () => translateToString('name'), candidates: ['name', 'nom', 'categorie', 'category'], required: true },
			{ key: 'parent', label: () => translateToString('parent_category'), candidates: ['parent', 'categorie parente', 'parent category'] },
			{ key: 'description', label: () => translateToString('description'), candidates: ['description'] },
		],
		'import.settings.categories_hint',
		rows => axios.post(`${URL_FOUNDATION}/api/categories/import`, { categories: rows }).then(res => res.data),
		onDone,
	);
}

export function downloadCsv(filename: string, headers: string[], rows: string[][]): void {
	const quote = (value: string) => `"${value.replace(/"/g, '""')}"`;
	const lines = [headers.map(quote).join(';'), ...rows.map(row => row.map(quote).join(';'))];
	const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
	const url = URL.createObjectURL(blob);
	const anchor = document.createElement('a');
	anchor.href = url;
	anchor.download = filename;
	anchor.click();
	URL.revokeObjectURL(url);
}

// the exports mirror the import columns, so a file exported here re-imports
// with every column auto-mapped
export function exportBrands(): void {
	getBrands().then(brands => downloadCsv(
		'brands.csv',
		[translateToString('name'), translateToString('description')],
		brands.map(brand => [brand.name ?? '', brand.description ?? '']),
	)).catch(console.error);
}

type RawCategoryNode = { name: string, description?: string, children?: RawCategoryNode[] };

export function exportCategories(): void {
	getCategories().then(res => {
		const rows: string[][] = [];
		// depth-first, parents before children: the file re-imports in one pass
		const walk = (nodes: RawCategoryNode[], parent: string) => {
			for (const node of nodes) {
				rows.push([node.name ?? '', parent, node.description ?? '']);
				walk(node.children ?? [], node.name ?? '');
			}
		};
		walk(res.data, '');
		downloadCsv('categories.csv', [translateToString('name'), translateToString('parent_category'), translateToString('description')], rows);
	}).catch(console.error);
}
